import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import {
  Building2,
  ArrowLeft,
  Upload,
  X,
  Loader2,
  MapPin,
  FileText,
  Image as ImageIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function NewProjectPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileInputRef = useRef(null);
  const { t } = useTranslation();

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState([]);
  const [dragOver, setDragOver] = useState(false);

  const [formData, setFormData] = useState({
    category_id: "",
    title: "",
    description: "",
    city: "",
    zip_code: "",
  });

  useEffect(() => {
    fetchCategories();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories?lang=${i18n.language?.startsWith('es') ? 'es' : 'en'}`);
      setCategories(response.data);
    } catch (error) {
      toast.error(t("project.failedLoadCategories"));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCategoryChange = (value) => {
    setFormData((prev) => ({ ...prev, category_id: value }));
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    handleFiles(files);
  };

  const handleFiles = (files) => {
    const validFiles = files.filter((file) => {
      if (!file.type.startsWith("image/")) {
        toast.error(`${file.name} is not an image`);
        return false;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is larger than 5MB`);
        return false;
      }
      return true;
    });

    if (images.length + validFiles.length > 5) {
      toast.error(t("project.maxImages"));
      return;
    }

    validFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setImages((prev) => [
          ...prev,
          { file, preview: e.target.result, name: file.name },
        ]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    handleFiles(files);
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.category_id || !formData.title || !formData.description || !formData.city || !formData.zip_code) {
      toast.error(t("project.fillRequired"));
      return;
    }

    setLoading(true);
    try {
      // Create project
      const projectRes = await axios.post(`${API}/projects`, formData);
      const projectId = projectRes.data.id;

      // Upload images if any
      if (images.length > 0) {
        const formDataImages = new FormData();
        images.forEach((img) => {
          formDataImages.append("images", img.file);
        });

        await axios.post(`${API}/projects/${projectId}/images`, formDataImages, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }

      toast.success(t("project.projectCreated"));
      navigate("/customer");
    } catch (error) {
      const message = error.response?.data?.detail || t("project.failedCreate");
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Link
              to="/customer"
              className="p-2 rounded-lg hover:bg-slate-100 transition-colors duration-200"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600" />
            </Link>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#0B1F3B] flex items-center justify-center">
                <Building2 className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <span className="font-bold text-[#0B1F3B]">MyProjectBidder</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-3xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-extrabold text-[#0B1F3B]" style={{ fontFamily: 'Manrope' }}>
            {t("project.requestEstimates")}
          </h1>
          <p className="text-slate-600 mt-2">
            {t("project.requestSubtitle")}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Category Selection */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                <FileText className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <h2 className="font-bold text-[#0B1F3B]">{t("project.projectDetails")}</h2>
                <p className="text-sm text-slate-500">{t("project.whatWork")}</p>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="category" className="text-[#0B1F3B] font-medium">
                  {t("project.serviceCategory")} *
                </Label>
                <Select onValueChange={handleCategoryChange} value={formData.category_id}>
                  <SelectTrigger className="mt-1.5 h-12" data-testid="category-select">
                    <SelectValue placeholder={t("project.selectCategory")} />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="title" className="text-[#0B1F3B] font-medium">
                  {t("project.projectTitle")} *
                </Label>
                <Input
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="e.g., Kitchen Renovation"
                  className="mt-1.5 h-12"
                  data-testid="project-title-input"
                />
              </div>

              <div>
                <Label htmlFor="description" className="text-[#0B1F3B] font-medium">
                  {t("project.projectDescription")} *
                </Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Describe your project in detail. Include dimensions, materials, timeline preferences, etc."
                  className="mt-1.5 min-h-[150px]"
                  data-testid="project-description-input"
                />
              </div>
            </div>
          </div>

          {/* Location */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <h2 className="font-bold text-[#0B1F3B]">{t("project.location")}</h2>
                <p className="text-sm text-slate-500">{t("project.whereProject")}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="city" className="text-[#0B1F3B] font-medium">
                  {t("profile.cityLabel")} *
                </Label>
                <Input
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="e.g., San Francisco"
                  className="mt-1.5 h-12"
                  data-testid="city-input"
                />
              </div>
              <div>
                <Label htmlFor="zip_code" className="text-[#0B1F3B] font-medium">
                  {t("profile.zipCode")} *
                </Label>
                <Input
                  id="zip_code"
                  name="zip_code"
                  value={formData.zip_code}
                  onChange={handleInputChange}
                  placeholder="e.g., 94102"
                  className="mt-1.5 h-12"
                  data-testid="zip-input"
                />
              </div>
            </div>
          </div>

          {/* Image Upload */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
                <ImageIcon className="w-5 h-5 text-[#D4AF37]" />
              </div>
              <div>
                <h2 className="font-bold text-[#0B1F3B]">{t("project.projectPhotos")}</h2>
                <p className="text-sm text-slate-500">{t("project.uploadUpTo5")}</p>
              </div>
            </div>

            <div
              className={`dropzone rounded-lg p-8 text-center cursor-pointer ${
                dragOver ? "drag-over" : ""
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              data-testid="image-dropzone"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handleFileSelect}
                className="hidden"
              />
              <Upload className="w-10 h-10 text-slate-400 mx-auto mb-3" />
              <p className="text-slate-600 font-medium">
                {t("project.dropImages")}
              </p>
              <p className="text-sm text-slate-500 mt-1">
                {t("project.imageLimit")}
              </p>
            </div>

            {images.length > 0 && (
              <div className="image-preview-grid mt-4">
                {images.map((img, idx) => (
                  <div key={idx} className="image-preview">
                    <img src={img.preview} alt={img.name} />
                    <button
                      type="button"
                      onClick={() => removeImage(idx)}
                      className="remove-btn"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit */}
          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate("/customer")}
              className="flex-1 h-12"
            >
              {t("common.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 h-12 gold-gradient text-[#0B1F3B] hover:brightness-110 font-bold"
              data-testid="submit-project-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t("project.creating")}
                </>
              ) : (
                t("project.submitRequest")
              )}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
}
